import { MRT_Row } from "material-react-table";
import type { Job } from "../../../../../api/types";
import { isFieldRequired } from "../../../../../utilities/jobValidation";
import { ColumnContext } from "../standard-column-types";
import { NextCropColumnViewModel } from "./NextCrop.column.types";

/**
 * Hook that provides the NextCropColumn ViewModel
 * @param context - The column context containing state and helpers
 * @returns ViewModel with all business logic functions
 */
export function useNextCropColumnViewModel(context: ColumnContext): NextCropColumnViewModel {
	/**
	 * Get current nextCrop value for job
	 * Uses row.original directly instead of O(n) jobs.find() lookup
	 */
	function getCurrentValue(job: Job): string | null {
		return job?.nextCrop || null;
	}

	/**
	 * Compute required state from context
	 */
	function getIsRequired(row: MRT_Row<Job>): boolean {
		const rowContext = context.getRowValidationContext(row);
		return isFieldRequired("nextCrop", rowContext);
	}

	/**
	 * Handle Cell value change
	 */
	function handleChange(jobId: string, newValue: string | null): void {
		const newJobs = [...context.jobs];
		const newJob = newJobs.find((j) => j.id === jobId);
		if (!newJob) return;

		const index = newJobs.indexOf(newJob);
		newJob.nextCrop = newValue as string | undefined;
		newJobs[index] = newJob;
		context.setJobs(newJobs);

		context.validateJobs([newJob], { updateState: true, clearPrevious: false });
	}

	/**
	 * Handle AggregatedCell group update
	 */
	function handleGroupChange(row: MRT_Row<Job>, newValue: string | null): void {
		const newJobs = context.updateJobGroup(row, context.jobs, (job) => {
			job.nextCrop = newValue as string | undefined;
		});

		context.setJobs(newJobs);
		context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
	}

	return {
		getCurrentValue,
		getIsRequired,
		handleChange,
		handleGroupChange,
	};
}
